// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Button,
  CheckIcon,
  ChevronDownIcon,
  Flex,
  List,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import type { AriaAttributes } from 'react';
import { useCallback } from 'react';

import { useIpadMediaQuery, useKioskSpacing } from '@jane/kiosk/hooks';

type SortOption<TId extends string> = { id: TId; label: string };

type SortButtonProps<
  TId extends string,
  TOption extends SortOption<TId>,
> = Pick<AriaAttributes, 'aria-label'> & {
  condensed?: boolean;
  currentSort?: { id: TId };
  filled?: boolean;
  onSort: (newSort: TOption) => void;
  options: TOption[];
};

type TransientProps = {
  filled?: boolean;
};

const StyledSortButton = styled(Button)<TransientProps>(({ filled }) => {
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  return {
    border: 'none',
    borderRadius: 16,
    maxHeight: isIpad ? 48 : 64,
    paddingBottom: getKioskSpacing(24, 12),
    paddingTop: getKioskSpacing(24, 12),
    ...(!filled && {
      background: 'unset',
    }),
  };
});

export const SortButton = <
  TId extends string,
  TOption extends SortOption<TId>,
>({
  currentSort,
  options,
  onSort,
  condensed,
  filled = true,
  'aria-label': ariaLabel,
}: SortButtonProps<TId, TOption>) => {
  const isIpad = useIpadMediaQuery();
  const textVariant = condensed ? 'small-body-regular' : 'body-semibold';
  const selectedSort = currentSort ?? options[0];

  const handleSortSelected = useCallback(
    (closePopover: PopoverContextProps['closePopover'], newSort: TOption) => {
      closePopover();
      onSort(newSort);
    },
    [onSort]
  );

  return (
    <Popover
      alignment={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      disableMobileStyling
      label="Sort options"
      ml={16}
      target={
        <StyledSortButton
          ariaLabel={ariaLabel}
          endIcon={<ChevronDownIcon size="sm" />}
          filled={filled}
          label={
            <Typography variant={textVariant}>
              {`${condensed ? '' : 'Sort: '}${
                options.find((option) => option.id === selectedSort.id)?.label
              }`}
            </Typography>
          }
          data-testid="sort-btn"
          variant="tertiary"
        />
      }
    >
      {({ closePopover }: PopoverContextProps) => (
        <Popover.Content padding={false}>
          <List label="Sort options" py={isIpad ? 8 : 16}>
            {options.map((option) => (
              <List.Item
                ariaLabel={`sort by ${option.label}`}
                onClick={() => handleSortSelected(closePopover, option)}
                key={option.id}
                px={24}
                py={12}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                >
                  <Typography>{option.label}</Typography>
                  {selectedSort.id === option.id && <CheckIcon />}
                </Flex>
              </List.Item>
            ))}
          </List>
        </Popover.Content>
      )}
    </Popover>
  );
};
